/* eslint-disable camelcase */
import { selectSkuSelectorEnabled } from 'ai/selectors/killSwitches/selectSkuSelectorEnabled';
import { MESSAGE_TYPES, RESPONSE_TYPES } from 'ai/constants/superchat';
import isValidSkuId from 'ai/utils/isValidSkuId';

const CONTRACT_ERROR_MESSAGE = 'SuperChat: Invalid SKU selector product contract';
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const VARIATION_CARD_STRING_FIELDS = ['image_url', 'list_price', 'sale_price', 'size'];

/** Returns the backend opt-in flag for every generated SuperChat socket payload. */
const getSkuSelectorPayloadFields = () => ({ sku_selector_wire_enabled: selectSkuSelectorEnabled() });

const stripSkuSelectorFields = product => {
    const {
        variations,
        pre_selected_sku_id,
        fallback_reason,
        fallback_text,
        variation_type,
        swatch_type,
        sku_selector_type,
        ...baseProduct
    } = product;

    return baseProduct;
};

const createContractIssues = () => ({
    invalidProductsCollection: false,
    invalidProductCount: 0,
    invalidVariationsCollectionCount: 0,
    invalidVariationCount: 0,
    duplicateVariationCount: 0,
    invalidVariationFieldCount: 0,
    invalidPreselectedSkuCount: 0
});

const hasContractIssues = issues =>
    issues.invalidProductsCollection ||
    issues.invalidProductCount > 0 ||
    issues.invalidVariationsCollectionCount > 0 ||
    issues.invalidVariationCount > 0 ||
    issues.duplicateVariationCount > 0 ||
    issues.invalidVariationFieldCount > 0 ||
    issues.invalidPreselectedSkuCount > 0;

const reportContractIssues = (issues, source) => {
    if (!hasContractIssues(issues)) {
        return;
    }

    // Do not log the product payload: chat product data may contain customer-specific
    // recommendations. Counts and the internal source are enough to diagnose drift.
    Sephora.logger.error(CONTRACT_ERROR_MESSAGE, {
        source,
        ...issues
    });
};

const normalizeVariation = (variation, issues) => {
    if (!isRecord(variation) || !isValidSkuId(variation.sku_id)) {
        issues.invalidVariationCount += 1;

        return null;
    }

    const normalizedVariation = { ...variation };

    VARIATION_CARD_STRING_FIELDS.forEach(field => {
        if (hasOwn(normalizedVariation, field) && normalizedVariation[field] != null && typeof normalizedVariation[field] !== 'string') {
            delete normalizedVariation[field];
            issues.invalidVariationFieldCount += 1;
        }
    });

    return normalizedVariation;
};

/**
 * Normalizes the product collection at the external-data boundary while keeping every
 * usable base product card. SKU selector enrichment is optional, so malformed enrichment
 * is removed instead of causing the entire recommendation response to fail.
 */
const normalizeSkuSelectorProducts = (
    products,
    { isEnabled = selectSkuSelectorEnabled(), source = 'unknown' } = {}
) => {
    const issues = createContractIssues();

    if (!Array.isArray(products)) {
        issues.invalidProductsCollection = true;
        reportContractIssues(issues, source);

        return [];
    }

    const normalizedProducts = [];

    products.forEach(product => {
        if (!isRecord(product)) {
            issues.invalidProductCount += 1;

            return;
        }

        if (!isEnabled) {
            normalizedProducts.push(stripSkuSelectorFields(product));

            return;
        }

        const normalizedProduct = { ...product };

        if (hasOwn(product, 'variations')) {
            if (Array.isArray(product.variations)) {
                const seenSkuIds = new Set();

                normalizedProduct.variations = product.variations.reduce((validVariations, variation) => {
                    const normalizedVariation = normalizeVariation(variation, issues);

                    if (!normalizedVariation) {
                        return validVariations;
                    }

                    if (seenSkuIds.has(normalizedVariation.sku_id)) {
                        issues.duplicateVariationCount += 1;

                        return validVariations;
                    }

                    seenSkuIds.add(normalizedVariation.sku_id);
                    validVariations.push(normalizedVariation);

                    return validVariations;
                }, []);
            } else {
                // An explicitly malformed collection is complete-but-unusable. An empty
                // array makes the selector non-renderable while preserving the base card.
                normalizedProduct.variations = [];
                issues.invalidVariationsCollectionCount += 1;
            }
        }

        if (hasOwn(product, 'pre_selected_sku_id')) {
            const variationSkuIds = new Set((normalizedProduct.variations || []).map(variation => variation.sku_id));
            const hasValidPreselection =
                isValidSkuId(product.pre_selected_sku_id) && variationSkuIds.has(product.pre_selected_sku_id);

            if (!hasValidPreselection) {
                delete normalizedProduct.pre_selected_sku_id;

                if (product.pre_selected_sku_id != null) {
                    issues.invalidPreselectedSkuCount += 1;
                }
            }
        }

        normalizedProducts.push(normalizedProduct);
    });

    reportContractIssues(issues, source);

    return normalizedProducts;
};

/**
 * Normalizes product collections on parsed recommendation frames without mutating the frame.
 */
const normalizeSkuSelectorMessage = (message, options = {}) => {
    if (!isRecord(message) || !isRecord(message.data)) {
        return message;
    }

    const productsField = message.response_type === MESSAGE_TYPES.PRODUCTS_RESPONSE
        ? 'products'
        : message.response_type === RESPONSE_TYPES.COMPLEMENTARY_PRODUCTS_RESPONSE
            ? 'complementary_products'
            : null;

    if (productsField === null || !hasOwn(message.data, productsField)) {
        return message;
    }

    return {
        ...message,
        data: {
            ...message.data,
            [productsField]: normalizeSkuSelectorProducts(message.data[productsField], options)
        }
    };
};

export {
    CONTRACT_ERROR_MESSAGE,
    getSkuSelectorPayloadFields,
    normalizeSkuSelectorMessage,
    normalizeSkuSelectorProducts
};
